import { useState } from 'react';
// @mui
import {
  Box,
  Button,
  Grid,
  Paper,
  Table,
  TableBody,
  TableContainer,
  TableRow,
  TableCell,
  Typography,
  Stack,
  styled,
  Divider,
  useTheme,
} from '@mui/material';
import QRCode from 'react-qr-code';
import { useSnackbar } from 'notistack';

import Iconify from '../../components/Iconify';
import useTable from '../../hooks/useTable';
import Scrollbar from '../../components/Scrollbar';
import { TableNoData, TableSkeleton, TableHeadCustom } from '../../components/table';

import conObj from '../../utils/connection-assistant';
import useLocales from '../../hooks/useLocales';
import TierTableRow from './TierTableRow';
import { fNumber } from '../../utils/formatNumber';

const Title = styled(Typography)(({ theme }) => ({
  fontSize: '1.2rem',
  color: '#d4af37',
  fontWeight: 500,
}));

const CustomText = styled(Typography)(({ theme }) => ({
  color: 'black',
  fontSize: 13,
  textOverflow: 'ellipsis',
  overflow: 'hidden',
  whiteSpace: 'nowrap',
}));

const ClaimButton = styled(Button)(({ theme }) => ({
  width: 'auto',
  background: '#C9A227',
  padding: '5px 10px',
  marginLeft: 'auto !important',
  marginRight: 'auto !important',
  color: 'white',
  fontWeight: 600,
  fontSize: 13,
  '&:hover': {
    backgroundColor: '#B8860B',
  },
}));

const NewMyReferral = ({ tableData, referralData, getMemberReferral }) => {
  const theme = useTheme();

  const { translate } = useLocales();

  const { enqueueSnackbar } = useSnackbar();

  const [isLoading, setIsLoading] = useState(false);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const { dense, order, orderBy, rowsPerPage, onSort } = useTable();

  const claimProfit = () => {
    if (parseFloat(referralData?.total_profit.value) - parseFloat(referralData?.total_profit_claim.value) <= 0) {
      enqueueSnackbar(translate('nothing_to_claim'), { variant: 'warning', autoHideDuration: 5000 });
      return;
    }

    setIsSubmitting(true);
    const requestData = {
      'history-amount':
        parseFloat(referralData?.total_profit.value) - parseFloat(referralData?.total_profit_claim.value),
    };
    conObj
      .post('members/claim_profit.json', requestData)
      .then((res) => {
        if (res.status === 200) {
          setIsSubmitting(false);
          getMemberReferral();
          enqueueSnackbar(res.data?.data.msg, { variant: 'success', autoHideDuration: 5000 });
        }
      })
      .catch((err) => {
        console.log(`Error: ${err.message}`);
        enqueueSnackbar(err.message, { variant: 'error', autoHideDuration: 5000 });
        setIsSubmitting(false);
      });
  };

  const denseHeight = dense ? 60 : 80;

  const isNotFound = !tableData.length || (!isLoading && !tableData.length);

  const TABLE_HEAD = [
    { id: 'tier', label: translate('tier'), align: 'left', sx: { boxShadow: 'unset !important' } },
    { id: 'commission', label: `${translate('commission')} (%)`, align: 'left' },
    { id: 'cnt', label: translate('referred_member'), align: 'left' },
    { id: 'profit', label: translate('profit_earned'), align: 'left', sx: { boxShadow: 'unset !important' } },
  ];

  return (
    <Box px={{ xs: 2, md: 20 }} paddingBottom={{ xs: 12, md: 10 }} paddingTop={{ xs: 2 }} bgcolor={'#000000'}>
      <Box mb={2}>
        <Box display="flex">
          <Box
            sx={{
              background: theme.palette.goldcity.gradient.main,
              borderTopLeftRadius: 10,
              borderTopRightRadius: 10,
            }}
            px={2}
            py={0.5}
          >
            <Typography>{translate('my_referral')}</Typography>
          </Box>
        </Box>
        <Divider sx={{ width: '100%', borderColor: theme.palette.goldcity.border.main }} />
      </Box>
      <Grid container spacing={2}>
        <Grid item xl={7} lg={7} md={7} sm={12} xs={12}>
          <Paper
            sx={{
              p: 2,
              mx: 'auto',
              position: 'relative',
              borderRadius: 2,
              border: '2px solid transparent',
              background:
                'linear-gradient(#333333, #0D0D0D) padding-box, ' +
                'linear-gradient(180deg, #FDD146 0%, #F09F0E 100%) border-box',
              color: '#fff',
              boxShadow: 'none',
            }}
          >
            <Stack direction="row" alignItems="center">
              <Title>{translate('my_referral')}</Title>
              <Box sx={{ flexGrow: 1 }} />

              <ClaimButton
                disabled={isSubmitting}
                startIcon={<Iconify icon={'ph:wallet-light'} sx={{ color: '#fff' }} />}
                onClick={() => claimProfit()}
              >
                {translate('claim_profit')}
              </ClaimButton>
            </Stack>

            <CustomText sx={{ marginTop: 2, opacity: 0.999, fontWeight: 600, color: '#FF9D00' }}>
              {translate('referral_note_on_x', { x: 'GoldCity99' })}
            </CustomText>

            <CustomText
              sx={{
                mt: 3,
                p: '5px 10px',
                background: '#d4af37',
                borderRadius: '5px',
                fontWeight: 500,
                fontSize: '1rem',
              }}
            >
              {translate('account_information')}
            </CustomText>
            <Box sx={{ mt: 1, pl: '5px' }}>
              <Stack direction="row">
                <CustomText sx={{ flexGrow: 1, fontSize: '1rem', color: '#FF9D00' }}>
                  {translate('available_profit')}
                </CustomText>
                <CustomText sx={{ fontSize: '1rem', color: '#FF9D00' }}>
                  {`${referralData?.total_profit?.currency_alias} ${fNumber(
                    referralData?.total_profit?.value - referralData?.total_profit_claim?.value
                  )}`}
                </CustomText>
              </Stack>

              <Stack direction="row" sx={{ mt: 1 }}>
                <CustomText sx={{ flexGrow: 1, color: '#FF9D00' }}>{translate('claimed_profit')}</CustomText>
                <CustomText sx={{ color: '#FF9D00' }}>
                  {`${referralData?.total_profit_claim?.currency_alias} ${fNumber(
                    referralData?.total_profit_claim?.value
                  )}`}
                </CustomText>
              </Stack>

              <Stack direction="row" sx={{ mt: 1 }}>
                <CustomText sx={{ flexGrow: 1, color: '#FF9D00' }}>{translate('all_time_profit')}</CustomText>
                <CustomText sx={{ color: '#FF9D00' }}>
                  {`${referralData?.total_profit?.currency_alias} ${fNumber(referralData?.total_profit?.value)}`}
                </CustomText>
              </Stack>
            </Box>

            <Box sx={{ mt: 3, width: '100%' }}>
              <Scrollbar>
                <TableContainer sx={{ minWidth: 500, position: 'relative' }}>
                  <Table size={'small'}>
                    <TableHeadCustom
                      order={order}
                      orderBy={orderBy}
                      headLabel={TABLE_HEAD}
                      rowCount={tableData.length}
                      onSort={onSort}
                      sx={{
                        background: '#d4af37',
                      }}
                    />

                    <TableBody
                      sx={{
                        '& .MuiTableCell-root': {
                          color: '#FF9D00 !important',
                        },
                      }}
                    >
                      {(isLoading ? [...Array(rowsPerPage)] : tableData).map((row, index) =>
                        row ? (
                          <TierTableRow key={row.id} row={row} />
                        ) : (
                          !isNotFound && <TableSkeleton key={index} sx={{ height: denseHeight }} />
                        )
                      )}
                      {tableData.length > 0 && (
                        <TableRow hover>
                          <TableCell />

                          <TableCell />

                          <TableCell>
                            <CustomText sx={{ fontWeight: 500, fontSize: '1rem', color: '#FF9D00' }}>
                              {translate('total_profit')}
                            </CustomText>
                          </TableCell>

                          <TableCell>
                            <CustomText sx={{ fontWeight: 500, fontSize: '1rem', color: '#FF9D00' }}>{`${
                              referralData?.total_profit?.currency_alias
                            } ${fNumber(
                              referralData?.total_profit?.value - referralData?.total_profit_claim?.value
                            )}`}</CustomText>
                          </TableCell>
                        </TableRow>
                      )}
                      <TableNoData isNotFound={isNotFound} />
                    </TableBody>
                  </Table>
                </TableContainer>
              </Scrollbar>
            </Box>
          </Paper>
        </Grid>
        <Grid item xl={5} lg={5} md={5} sm={12} xs={12}>
          <Paper
            sx={{
              p: 2,
              mx: 'auto',
              position: 'relative',
              borderRadius: 2, // 16px
              border: '2px solid transparent',
              background:
                'linear-gradient(#333333, #0D0D0D) padding-box, ' +
                'linear-gradient(180deg, #FDD146 0%, #F09F0E 100%) border-box',
              color: '#fff',
              boxShadow: 'none',
            }}
          >
            <Stack direction="row" alignItems="center">
              <CustomText sx={{ fontSize: '1.2rem', fontWeight: 500, flexGrow: 1, color: '#FF9D00' }}>
                {translate('invite_friends')}
              </CustomText>
              <CustomText sx={{ cursor: 'pointer', fontSize: '.75rem', textDecoration: 'underline', color: 'white' }}>
                {translate('how_it_work')}
              </CustomText>
            </Stack>

            <CustomText sx={{ mt: 1, color: '#FF9D00' }}>{translate('invite_note')}</CustomText>

            <Box
              sx={{
                background: 'white',
                p: 2,
                height: 'auto',
                mt: 5,
                mx: 'auto',
                maxWidth: 200,
                width: '100%',
              }}
            >
              <QRCode
                size={256}
                style={{ height: 'auto', maxWidth: '100%', width: '100%' }}
                value={`${window.location.origin}?aff=${referralData?.referral_code}`}
                viewBox={`0 0 256 256`}
              />
            </Box>

            <Stack direction="column" justifyContent="center" alignItems="center" sx={{ mt: 1 }}>
              <CustomText sx={{ fontSize: '1rem', color: '#FF9D00' }}>{translate('share_via')}:</CustomText>
              <Stack direction="row" spacing={1}>
                <Iconify
                  onClick={() =>
                    window.open(
                      `https://www.facebook.com/share.php/?u=${window.location.origin}?aff=${referralData?.referral_code}`,
                      '_blank'
                    )
                  }
                  icon={'devicon:facebook'}
                  width="25px"
                  height="25px"
                  sx={{ cursor: 'pointer' }}
                />
                <Iconify
                  onClick={() =>
                    window.open(
                      `https://twitter.com/home/?status=${window.location.origin}?aff=${referralData?.referral_code}`
                    )
                  }
                  icon={'skill-icons:twitter'}
                  width="25px"
                  height="25px"
                  sx={{ color: '#fff', cursor: 'pointer' }}
                />
                <Iconify
                  onClick={() =>
                    window.open(`https://wa.me/?text=${window.location.origin}?aff=${referralData?.referral_code}`)
                  }
                  icon={'logos:whatsapp-icon'}
                  width="25px"
                  height="25px"
                  sx={{ cursor: 'pointer' }}
                />
              </Stack>
            </Stack>

            <Box sx={{ mt: 3 }}>
              <Box>
                <CustomText sx={{ fontSize: '1rem', color: '#FF9D00' }}>{translate('referral_code')}</CustomText>
                <Stack
                  direction="row"
                  alignItems="center"
                  sx={{
                    background: 'rgb(255, 255, 255, .5)',
                    borderRadius: '5px',
                    border: '1px solid #ccc',
                    p: '5px 10px',
                    mt: 1,
                    cursor: 'pointer',
                    color: '#fff',
                  }}
                  onClick={() => {
                    enqueueSnackbar(`${translate('copied')}!`, { variant: 'success', autoHideDuration: 5000 });
                    navigator.clipboard.writeText(referralData?.referral_code);
                  }}
                >
                  <CustomText sx={{ width: '100%', fontSize: '1rem' }}>{referralData?.referral_code}</CustomText>

                  <Iconify icon={'solar:copy-bold'} sx={{ ml: 1, color: '#ffbb4dff ' }} />
                </Stack>
              </Box>

              <Box sx={{ mt: 2 }}>
                <CustomText sx={{ fontSize: '1rem', color: '#FF9D00' }}>{translate('referral_link')}</CustomText>
                <Stack
                  direction="row"
                  alignItems="center"
                  sx={{
                    background: 'rgb(255, 255, 255, .5)',
                    borderRadius: '5px',
                    p: '5px 10px',
                    mt: 1,
                    cursor: 'pointer',
                    color: '#fff',
                    border: '1px solid #ccc',
                  }}
                  onClick={() => {
                    enqueueSnackbar(`${translate('copied')}!`, { variant: 'success', autoHideDuration: 5000 });
                    navigator.clipboard.writeText(`${window.location.origin}?aff=${referralData?.referral_code}`);
                  }}
                >
                  <CustomText
                    sx={{ width: '100%', fontSize: '1rem' }}
                  >{`${window.location.origin}?aff=${referralData?.referral_code}`}</CustomText>

                  <Iconify icon={'solar:copy-bold'} sx={{ ml: 1, color: '#ffbb4dff ' }} />
                </Stack>
              </Box>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default NewMyReferral;
